import { useEffect, useState, lazy, Suspense } from 'react'
import type { CVDocument } from '../../stores/cvGenerationStore'
import { csrfHeaders } from '../../lib/api'
import { cvDocumentToHtml } from './SuperDocEditor'

const BASE_URL = import.meta.env.VITE_API_URL ?? ''

const SDEditor = lazy(() =>
  import('@superdoc-dev/react').then((m) => ({ default: m.SuperDocEditor })),
)

interface MobilePreviewProps {
  cvDoc: CVDocument
  onBack: () => void
  onApprove: () => void
  approving: boolean
}

type PreviewState =
  | { status: 'loading' }
  | { status: 'ready'; docx: Blob }
  | { status: 'fallback' }

export default function MobilePreview({
  cvDoc,
  onBack,
  onApprove,
  approving,
}: MobilePreviewProps) {
  const [preview, setPreview] = useState<PreviewState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    fetch(`${BASE_URL}/api/v1/cv/render-preview`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
      body: JSON.stringify({ cv_document: cvDoc }),
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error('render failed'))))
      .then((blob) => {
        if (!cancelled) setPreview({ status: 'ready', docx: blob })
      })
      .catch(() => {
        if (!cancelled) setPreview({ status: 'fallback' })
      })
    return () => {
      cancelled = true
    }
  }, [cvDoc])

  const fallbackHtml = cvDocumentToHtml(cvDoc)

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#dac2b6] bg-white flex-shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-[#545f6e] hover:text-[#833c0b] transition-colors"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Back to edit
        </button>
        <span className="text-sm font-semibold text-[#003d60]">Preview</span>
        <div className="w-24" />
      </div>

      {/* Document viewer — overflow-x intentionally unrestricted to allow pinch-zoom */}
      <div
        className="flex-1 overflow-y-auto"
        style={{ touchAction: 'auto' }}
      >
        {preview.status === 'loading' && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <span className="animate-spin inline-block w-6 h-6 border-2 border-[#833c0b]/30 border-t-[#833c0b] rounded-full" />
            <p className="text-sm text-[#545f6e]">Rendering preview…</p>
          </div>
        )}

        {preview.status === 'ready' && (
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-20">
                <span className="animate-spin inline-block w-6 h-6 border-2 border-[#833c0b]/30 border-t-[#833c0b] rounded-full" />
              </div>
            }
          >
            <SDEditor
              documentMode="viewing"
              document={preview.docx}
              disableContextMenu
              hideToolbar
              className="superdoc-cv-preview"
            />
          </Suspense>
        )}

        {preview.status === 'fallback' && (
          <div
            className="p-6 text-sm leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_li]:my-1"
            dangerouslySetInnerHTML={{ __html: fallbackHtml }}
          />
        )}

        <p className="text-center text-[10px] text-[#877369] py-3 pb-6">
          Pinch to zoom
        </p>
      </div>

      {/* Bottom action bar */}
      <div className="flex gap-3 p-4 border-t border-[#dac2b6] bg-white flex-shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-3 text-sm font-medium border border-[#dac2b6] rounded text-[#545f6e] hover:bg-[#f4f3f0] transition-colors"
        >
          ← Back to edit
        </button>
        <button
          type="button"
          onClick={onApprove}
          disabled={approving}
          className="flex-1 py-3 text-sm font-bold text-white bg-[#833c0b] rounded uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
        >
          {approving ? (
            <span className="flex items-center justify-center gap-2">
              <span className="animate-spin inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
              Approving…
            </span>
          ) : (
            'Approve & Export →'
          )}
        </button>
      </div>
    </div>
  )
}
